import { useState, useEffect, useCallback } from 'react';
import { vendorApi } from '../../api';
import { toast } from 'sonner';
import {
  Plus, Search, Building2, MoreVertical, KeyRound, Loader2, Eye, EyeOff, Edit2,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function VendorClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [resetClient, setResetClient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await vendorApi.getClients();
      setClients(data || []);
    } catch (e) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter(c => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return [c.name, c.email, c.business_key].some(s => (s || '').toLowerCase().includes(q));
  });

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="vendor-clients-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">Businesses you've onboarded — they log in with their key.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="vendor-create-client-btn">
          <Plus size={16} /> New Client
        </Button>
      </header>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clients…" className="pl-9 h-9 text-sm" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-sm text-muted-foreground">
          <Building2 size={36} className="mx-auto mb-3 text-muted-foreground/50" />
          {clients.length === 0 ? 'No clients yet — create your first one above.' : 'No matches.'}
        </CardContent></Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <Card key={c.id} data-testid={`vendor-client-card-${c.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold truncate">{c.name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical size={14} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="gap-2" onClick={() => setResetClient(c)}>
                        <KeyRound size={13} /> Reset admin password
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <KeyRound size={11} /> <code className="font-mono text-foreground">{c.business_key}</code>
                  </div>
                  <div>Reviews: <span className="text-foreground">{c.review_count ?? 0}</span></div>
                  <div>Admin: <span className="text-foreground">{c.admin_user?.email || '—'}</span></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateClientDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={load} />
      <ResetPasswordDialog client={resetClient} onClose={() => setResetClient(null)} />
    </div>
  );
}

function CreateClientDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', business_type: 'hotel', email: '',
    admin_name: '', admin_email: '', admin_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!open) setForm({ name: '', business_type: 'hotel', email: '', admin_name: '', admin_email: '', admin_password: '' });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error('Business name + email required');
    if (form.admin_password.length < 8) return toast.error('Admin password must be ≥ 8 chars');
    setSaving(true);
    try {
      const { data } = await vendorApi.createClient({ ...form, enabled_platforms: [] });
      toast.success(`Client created — key: ${data.business_key}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100vh-1rem)] sm:max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="vendor-create-client-dialog">
        <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle>New Client</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Business Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Business Type</Label>
              <select value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm">
                <option value="hotel">Hotel</option>
                <option value="restaurant">Restaurant</option>
                <option value="retail">Retail</option>
                <option value="healthcare">Healthcare</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Business Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <hr className="border-border" />
            <div className="space-y-1.5"><Label className="text-xs">Admin Name *</Label>
              <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Admin Email *</Label>
              <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required /></div>
            <div className="space-y-1.5"><Label className="text-xs">Admin Password *</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} required className="pr-9" />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter className="px-5 sm:px-6 py-3 sm:py-4 border-t border-border shrink-0 gap-2 bg-background">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ client, onClose }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!client) setPw(''); }, [client]);
  if (!client) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be ≥ 8 chars');
    setSaving(true);
    try {
      await vendorApi.resetClientPassword(client.id, { new_password: pw });
      toast.success('Password reset');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Reset Admin Password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-muted-foreground">For <strong>{client.admin_user?.email || client.name}</strong></p>
          <Input type="text" placeholder="New password (≥ 8 chars)" value={pw} onChange={(e) => setPw(e.target.value)} />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Reset
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
